import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
  title: string;
  value: number;
  icon: string;
  color: string;
  change: number;
  changeType: 'positive' | 'negative';
  isPercentage?: boolean;
  isTime?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon, 
  color, 
  change, 
  changeType,
  isPercentage = false,
  isTime = false
}) => {
  const { t } = useTranslation();
  
  const formatValue = () => {
    // Handle invalid or missing values
    const safeValue = isNaN(value) || value === null || value === undefined ? 0 : value;
    
    if (isTime) {
      const minutes = Math.floor(safeValue / 60);
      const seconds = Math.floor(safeValue % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    if (isPercentage) {
      return `${safeValue}%`;
    }
    
    return safeValue.toLocaleString();
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <motion.div variants={item}>
      <Card className="overflow-visible">
        <CardBody className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-default-500 text-sm">{title}</p>
              <h3 className="text-2xl font-semibold mt-1">
                {isTime ? (
                  formatValue()
                ) : (
                  <CountUp 
                    end={isNaN(value) ? 0 : value} 
                    duration={2} 
                    separator="," 
                    suffix={isPercentage ? '%' : ''}
                  />
                )}
              </h3>
              {change !== 0 && (
                <div className={`flex items-center mt-2 text-sm ${
                  changeType === 'positive' ? 'text-success' : 'text-danger'
                }`}>
                  <Icon 
                    icon={changeType === 'positive' ? 'lucide:trending-up' : 'lucide:trending-down'} 
                    width={16} 
                    className="mr-1"
                  />
                  <span>
                    {change > 0 && '+'}{change}{isPercentage ? '%' : ''} {t('thisMonth')}
                  </span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-lg ${color}`}>
              <Icon icon={icon} width={24} />
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};